import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { FlexBox } from '@/components/atom/FlexBox';
import { StyledText } from '@/components/atom/Text';
import useEnv from '@/hooks/useEnv';
import { formatRate } from '@/libs/formatNumber';
import { Theme } from '@/styles/theme';

const IndicatorContainer = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  line-height: 1.25;
`;

const Bar = styled.div<{ $amount: number; $maxAmount: number }>`
  width: 100%;
  height: ${(p) => p.theme.size.xxs};
  border-radius: 2px;
  background-color: #ffffff14;
  position: relative;
  &::before {
    content: '';
    position: absolute;
    display: block;
    border-radius: 2px;
    top: 0;
    bottom: 0;
    left: 0;
    width: ${(p) => p.$amount}%;
    background-image: linear-gradient(
      to right,
      ${(p) => `${p.theme.colors.green} ${(p.$maxAmount / p.$amount) * 100}%`},
      ${(p) => `${p.theme.colors.orange} ${(p.$maxAmount / p.$amount) * 100}%`},
      ${(p) => p.theme.colors.orange}
    );
    ${(p) => p.theme.bg.green};
  }
`;

const Pin = styled.div<{ $left: number; $color?: keyof Theme['colors'] }>`
  position: absolute;
  width: 1px;
  height: calc(100% + 1px);
  top: -0.5px;
  ${(p) => p.theme.bg[p.$color ?? 'textWhite']};
  left: max(2px, min(${(p) => p.$left}%, calc(100% - 2px)));
  ${(p) => p.theme.rounded.full};
  z-index: 1;
`;

const IndicatorBox = styled.div<{
  $left?: number;
  $right?: number;
  $color?: keyof Theme['colors'];
  $align?: 'center' | 'flex-start' | 'flex-end';
  $alignSelf?: 'center' | 'flex-start' | 'flex-end';
}>`
  display: inline-flex;
  position: absolute;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
  flex-direction: column;
  top: 0;
  text-align: center;

  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme, $color }) => theme.colors[$color ?? 'textWhite']};
  ${({ $left }) =>
    $left &&
    css`
      left: ${$left}%;
    `};
  ${({ $align }) =>
    $align &&
    css`
      align-items: ${$align};
    `};
  ${({ $alignSelf }) =>
    $alignSelf === 'center' &&
    css`
      transform: translateX(-50%);
    `};
  ${({ $alignSelf }) =>
    !$alignSelf &&
    css`
      transform: translateX(-50%);
    `};
  ${({ $alignSelf }) =>
    $alignSelf === 'flex-end' &&
    css`
      transform: translateX(-100%);
    `};
  ${({ $right }) => ($right ? `right: ${$right}%;` : '')};
  ${({ theme }) => theme.font.bold};

  &:first-of-type {
    position: relative;
  }
`;

interface Props {
  currentLTV: number;
  maxLTV: number;
  liquidation: number;
}

export default function LTVVisualizeBar({
  currentLTV,
  maxLTV,
  liquidation,
}: Props) {
  const { t } = useTranslation('dashboard');

  const { languageId } = useEnv();

  const alignBox = useMemo(() => {
    if (currentLTV < 0.1 && currentLTV > 0) {
      return 'flex-start';
    } else if (currentLTV > 99) {
      return 'flex-end';
    } else {
      return 'center';
    }
  }, [currentLTV]);
  const displayLiquidation = useMemo(() => {
    const limit = languageId === 'ko' ? 90 : 80;

    return liquidation > limit ? limit : liquidation;
  }, [languageId, liquidation]);

  return (
    <FlexBox $flex $direction="column" $gap="xxxs">
      <IndicatorContainer>
        <IndicatorBox
          $left={currentLTV}
          $color={
            currentLTV > 0
              ? currentLTV > maxLTV
                ? 'orange'
                : 'green'
              : 'textWhite'
          }
          $align={alignBox}
          $alignSelf={alignBox}
        >
          {formatRate(currentLTV)}%
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="8"
            height="7"
            fill="none"
            viewBox="0 0 8 7"
          >
            <path
              fill="currentColor"
              d="M4.894 6.211a1 1 0 0 1-1.788 0L.724 1.447A1 1 0 0 1 1.618 0h4.764a1 1 0 0 1 .894 1.447L4.894 6.211Z"
            />
          </svg>
        </IndicatorBox>
      </IndicatorContainer>
      <Bar $amount={currentLTV} $maxAmount={maxLTV}>
        <Pin $left={maxLTV} />
        <Pin $left={liquidation} $color="orange" />
      </Bar>
      <IndicatorContainer>
        <IndicatorBox $left={maxLTV}>
          MAX
          <StyledText $fontSize="xs" $fontWeight="regular">
            {maxLTV}%
          </StyledText>
        </IndicatorBox>
        <IndicatorBox $left={displayLiquidation} $color="orange">
          {t('liquidationThreshold')}
          <StyledText $fontSize="xs" $fontWeight="regular" $textColor="orange">
            {liquidation}%
          </StyledText>
        </IndicatorBox>
      </IndicatorContainer>
    </FlexBox>
  );
}
